import { useState, useEffect, useRef } from 'react';

import {
  Field,
  FieldRenderProps,
  FormRenderProps,
  FormMeta,
  ValidateTrigger,
} from '@flowgram.ai/free-layout-editor';
import { JsonSchemaEditor } from '@flowgram.ai/form-materials';
import {
  TextArea,
  Button,
  Radio,
  RadioGroup,
  InputNumber,
  Input,
  Spin,
  Tag,
  Toast,
} from '@douyinfe/semi-ui';
import { IconHelpCircle, IconCopy } from '@douyinfe/semi-icons';

import { getFieldValue, setFieldValue } from '../../utils/common';
import { FlowNodeJSON, JsonSchema } from '../../typings';
import { getFeishuTableMetaData } from '../../https/feishu-api';
import { useIsSidebar } from '../../hooks';
import { FormHeader, FormContent, FormOutputs, FormItem } from '../../form-components';

// 模拟推送飞书表的函数
const pushToFeishuSheet = async (link: string) => {
  try {
    // 这里应该是实际的推送逻辑，当前为模拟
    console.log(`尝试推送数据到飞书表，链接: ${link}`);
    return '数据推送成功';
  } catch (error) {
    throw error;
  }
};

export const renderForm = ({ form }: FormRenderProps<FlowNodeJSON>) => {
  const isSidebar = useIsSidebar();

  // 从表单中获取初始数据
  const getInitialDataMode = () => {
    const dataModeFromForm = getFieldValue('dataMode', form);
    if (dataModeFromForm) {
      return dataModeFromForm;
    }

    // 如果没有 dataMode，尝试从 config 中读取
    const config = getFieldValue('config', form);
    if (config && typeof config === 'object') {
      if (config.autoArchiveFeishuTable) {
        return 'autoArchive';
      } else if (config.deleteWholeFeishuTableDataBeforeRun) {
        return 'clearData';
      }
    }

    return 'clearData';
  };

  const getInitialArchiveThreshold = () => {
    const thresholdFromForm = getFieldValue('archiveThreshold', form);
    if (thresholdFromForm) {
      return Number(thresholdFromForm);
    }

    const config = getFieldValue('config', form);
    if (config?.archiveFeishuTableConfig?.maxRowCount) {
      return config.archiveFeishuTableConfig.maxRowCount;
    }

    return 1000;
  };

  const getInitialArchivePrefix = () => {
    const prefixFromForm = getFieldValue('archivePrefix', form);
    if (prefixFromForm) {
      return prefixFromForm;
    }

    const config = getFieldValue('config', form);
    if (config?.archiveFeishuTableConfig?.prefixName) {
      return config.archiveFeishuTableConfig.prefixName;
    }

    return '归档_';
  };

  const [link, setLink] = useState(getFieldValue('link', form));
  const [dataMode, setDataMode] = useState(getInitialDataMode());
  const [archiveThreshold, setArchiveThreshold] = useState(getInitialArchiveThreshold());
  const [archivePrefix, setArchivePrefix] = useState(getInitialArchivePrefix());
  const [pushResult, setPushResult] = useState('');

  // 飞书表格元数据相关状态
  const [isLoadingMetaData, setIsLoadingMetaData] = useState(false);
  const [metaDataError, setMetaDataError] = useState<string | null>(null);
  const [tableMetaData, setTableMetaData] = useState<any>(null);
  const prevFeishuUrlRef = useRef<string>('');

  // 复制单个字段名
  const copyFieldName = async (fieldName: string) => {
    try {
      await navigator.clipboard.writeText(fieldName);
      Toast.success(`已复制字段名: ${fieldName}`);
    } catch (error) {
      Toast.error('复制失败');
    }
  };

  // 复制所有字段名
  const copyAllFieldNames = async () => {
    if (!tableMetaData?.columnNames || tableMetaData.columnNames.length === 0) {
      Toast.warning('没有可复制的字段名');
      return;
    }
    try {
      const allFields = tableMetaData.columnNames.join(', ');
      await navigator.clipboard.writeText(allFields);
      Toast.success(`已复制所有字段名: ${allFields}`);
    } catch (error) {
      Toast.error('复制失败');
    }
  };

  // 处理飞书表格链接失去焦点事件
  const handleFeishuUrlBlur = async () => {
    const currentUrl = link?.trim();
    setMetaDataError(null);

    // 如果URL为空或与上次相同，则不处理
    if (!currentUrl) {
      return;
    }

    // 简单验证URL格式
    if (!currentUrl.startsWith('http')) {
      setMetaDataError('请输入有效的飞书表格链接，应以http或https开头');
      return;
    }

    // 如果URL与上次相同，则不重复请求
    if (currentUrl === prevFeishuUrlRef.current) {
      return;
    }

    // 更新上次的URL值
    prevFeishuUrlRef.current = currentUrl;

    // 开始加载
    setIsLoadingMetaData(true);
    setTableMetaData(null);

    try {
      // 调用接口获取表格元数据
      const response = await getFeishuTableMetaData({ url: currentUrl });

      // 确保正确获取 data 字段
      if (response && response.data) {
        // 验证返回的数据格式
        if (!response.data.tableName || !Array.isArray(response.data.columnNames)) {
          throw new Error('表格元数据格式不完整');
        }
        setTableMetaData(response.data);
        // 保存元数据到表单
        setFieldValue('objToken', response.data.objToken, form);
        setFieldValue('tableId', response.data.tableId, form);
      } else {
        throw new Error('返回数据格式不正确');
      }
    } catch (error: any) {
      console.error('获取表格元数据失败:', error);
      setMetaDataError(`获取表格元数据失败: ${error?.message || '请检查表格链接是否正确'}`);
      setTableMetaData(null);
    } finally {
      setIsLoadingMetaData(false);
    }
  };

  // 当dataMode切换到autoArchive时，确保form中有默认值
  useEffect(() => {
    if (dataMode === 'autoArchive') {
      if (!getFieldValue('archiveThreshold', form)) {
        setFieldValue('archiveThreshold', '1000', form);
      }
      if (!getFieldValue('archivePrefix', form)) {
        setFieldValue('archivePrefix', '归档_', form);
      }
    }
    setFieldValue('dataMode', dataMode, form);
  }, [dataMode, form]);

  // 组件挂载时检查是否已有飞书链接，如果有则自动获取元数据
  useEffect(() => {
    const initialLink = getFieldValue('link', form);
    const savedObjToken = getFieldValue('objToken', form);
    const savedTableId = getFieldValue('tableId', form);

    if (initialLink && initialLink.trim()) {
      // 如果有保存的元数据，先恢复显示
      if (savedObjToken && savedTableId) {
        // 从保存的字段重构元数据对象
        const reconstructedMetaData = {
          objToken: savedObjToken,
          tableId: savedTableId,
          tableName: '', // 这些字段需要重新获取
          viewId: '',
          columnNames: [],
        };
        setTableMetaData(reconstructedMetaData);
      }

      // 延迟执行以确保状态已更新
      setTimeout(() => {
        handleFeishuUrlBlur();
      }, 100);
    }
  }, []);

  if (isSidebar) {
    return (
      <>
        <FormHeader />
        <FormContent>
          <Field
            name="link"
            render={({ field }) => (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <span>飞书表格链接</span>
                  <IconHelpCircle
                    style={{ marginLeft: 4, color: '#1890ff', cursor: 'pointer' }}
                    title="填入后会自动获取飞书表格的名称和字段列表"
                  />
                </div>
                <TextArea
                  {...field}
                  value={link}
                  onChange={(value) => {
                    setLink(value);
                    setFieldValue('link', value, form);
                  }}
                  onBlur={handleFeishuUrlBlur}
                  placeholder="请输入飞书表格链接"
                />

                {/* 表格元数据展示区域 */}
                {isLoadingMetaData && (
                  <div
                    style={{ marginTop: 12, display: 'flex', alignItems: 'center', color: '#666' }}
                  >
                    <Spin size="small" style={{ marginRight: 8 }} />
                    <span>正在获取表格元数据...</span>
                  </div>
                )}

                {!isLoadingMetaData && metaDataError && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 8,
                      backgroundColor: '#fff2f0',
                      border: '1px solid #ffccc7',
                      borderRadius: 4,
                      color: '#ff4d4f',
                    }}
                  >
                    {metaDataError}
                  </div>
                )}

                {!isLoadingMetaData && !metaDataError && tableMetaData && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 'bold', color: '#52c41a' }}>表格名称: </span>
                      <span>{tableMetaData.tableName}</span>
                    </div>
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>表头: </span>
                        {tableMetaData.columnNames && tableMetaData.columnNames.length > 0 && (
                          <Button
                            icon={<IconCopy />}
                            size="small"
                            type="tertiary"
                            onClick={copyAllFieldNames}
                            style={{ fontSize: '12px' }}
                          >
                            复制全部
                          </Button>
                        )}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {tableMetaData.columnNames && tableMetaData.columnNames.length > 0 ? (
                          tableMetaData.columnNames.map((column: string, index: number) => (
                            <div
                              key={index}
                              style={{
                                display: 'inline-block',
                                marginRight: 4,
                                marginBottom: 4,
                              }}
                              title={`点击复制字段名: ${column}`}
                            >
                              <Tag
                                color="blue"
                                style={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                                onClick={() => copyFieldName(column)}
                              >
                                {column}
                                <IconCopy style={{ marginLeft: 4, fontSize: '12px' }} />
                              </Tag>
                            </div>
                          ))
                        ) : (
                          <span style={{ color: '#999' }}>无表头数据</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          />

          <Field
            name="dataMode"
            render={({ field }) => (
              <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>数据处理模式</div>
                <RadioGroup
                  {...field}
                  value={dataMode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDataMode(value);
                    setFieldValue('dataMode', value, form);
                  }}
                  direction="vertical"
                >
                  <Radio value="clearData">运行前删除飞书表格所有数据</Radio>
                  <Radio value="autoArchive">自动归档飞书表格</Radio>
                </RadioGroup>
              </div>
            )}
          />

          {dataMode === 'autoArchive' && (
            <>
              <Field
                name="archiveThreshold"
                render={({ field }) => (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>触发归档的行数阈值</div>
                    <InputNumber
                      {...field}
                      value={archiveThreshold}
                      onChange={(value) => {
                        const numValue = Number(value);
                        setArchiveThreshold(numValue);
                        setFieldValue('archiveThreshold', String(numValue), form);
                      }}
                      min={1}
                      placeholder="请输入行数阈值"
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
              />

              <Field
                name="archivePrefix"
                render={({ field }) => (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>归档表前缀名</div>
                    <Input
                      {...field}
                      value={archivePrefix}
                      onChange={(value) => {
                        setArchivePrefix(value);
                        setFieldValue('archivePrefix', value, form);
                      }}
                      placeholder="请输入归档表前缀"
                    />
                  </div>
                )}
              />
            </>
          )}

          {pushResult && <div>推送结果: {pushResult}</div>}
        </FormContent>
      </>
    );
  }
  return (
    <>
      <FormHeader />
      <FormContent>
        <FormOutputs />
        <FormItem name="Link" type="string">
          {link}
        </FormItem>
        <FormItem name="DataMode" type="string">
          {dataMode === 'clearData' ? '运行前删除数据' : '自动归档'}
        </FormItem>
        {dataMode === 'autoArchive' && (
          <>
            <FormItem name="ArchiveThreshold" type="number">
              {archiveThreshold}
            </FormItem>
            <FormItem name="ArchivePrefix" type="string">
              {archivePrefix}
            </FormItem>
          </>
        )}
      </FormContent>
    </>
  );
};

export const formMeta: FormMeta<FlowNodeJSON> = {
  render: renderForm,
  validateTrigger: ValidateTrigger.onChange,
  validate: {
    fields: ({ value }) => {
      const linkField = value.find((field: any) => field.key === 'link');
      const dataModeField = value.find((field: any) => field.key === 'dataMode');
      const archiveThresholdField = value.find((field: any) => field.key === 'archiveThreshold');
      const archivePrefixField = value.find((field: any) => field.key === 'archivePrefix');

      if (!linkField?.value) return '飞书表链接不能为空';

      if (dataModeField?.value === 'autoArchive') {
        if (!archiveThresholdField?.value || archiveThresholdField.value <= 0) {
          return '触发归档的行数阈值必须大于0';
        }
        if (!archivePrefixField?.value) {
          return '归档表前缀名不能为空';
        }
      }

      return undefined;
    },
  },
};
